import { c as createEmitter, i as isUndefined, a as isNull, b as isString, d as isNil } from './create-emitter-6d414a88.esm.js';
export { e as isArray, f as isError, g as isFunction, d as isNil, h as isNotNil, a as isNull, j as isNumber, k as isObject, l as isPromise, m as isRegexp, b as isString, n as isSymbol, i as isUndefined, t as typeOf } from './create-emitter-6d414a88.esm.js';
import cookie from 'js-cookie';
export { default as cookie } from 'js-cookie';
import validator from 'validator';
import memoize from 'just-memoize';
export { default as memoize } from 'just-memoize';
import baseSlugify from 'just-kebab-case';
import { useState, useEffect } from 'react';
export { default as capitalize } from 'just-capitalize';
export { default as clamp } from 'just-clamp';
export { default as compare } from 'just-compare';
export { default as debounce } from 'just-debounce-it';
export { default as extend } from 'just-extend';
export { default as filter } from 'just-filter-object';
export { default as pick } from 'just-pick';
export { default as prune } from 'just-prune';
export { default as set } from 'just-safe-set';
export { default as shuffle } from 'just-shuffle';
export { default as split } from 'just-split';
export { default as throttle } from 'just-throttle';
export { default as truncate } from 'just-truncate';
export { useCopyToClipboard, useDebounce, useInterval, useIsMounted, useLocalStorage, useMediaQuery, useSsr, useTimeout } from 'usehooks-ts';
export { v4 as uuid } from 'uuid';

let Level = /*#__PURE__*/function (Level) {
  Level[Level["Log"] = 0] = "Log";
  Level[Level["Info"] = 1] = "Info";
  Level[Level["Warn"] = 2] = "Warn";
  Level[Level["Error"] = 3] = "Error";
  return Level;
}({});
let Type = /*#__PURE__*/function (Type) {
  Type["Error"] = "error";
  Type["Info"] = "info";
  Type["Log"] = "log";
  Type["Warn"] = "warn";
  return Type;
}({});

/**
 * @remarks The {@link createLogger `createLogger()`} factory leverages the
 * {@link createEmitter `createEmitter()`} factory to build a logger. As a convenience, We have
 * already added subscriptions that leverage the native console api. Additionally, one has the
 * ability to add additional subscriptions to the logger if they so choose. Here are some things to
 * note about the `createLogger()` api before you start using it:
 *   1. All logger methods are asynchronous and return a promise.
 *   2. In production environments, the logger will only log errors.
 *   3. You can enable/disable all subscriptions to the logger.
 *   4. It works on both the client and server.
 *
 * The api for the logger includes the following methods:
 *   - {@link Logger.error `logger.log()`} - Logs a message to the console.
 *   - {@link Logger.info `logger.info()`} - Logs an info message to the console.
 *   - {@link Logger.warn `logger.warn()`} - Logs a warning message to the console.
 *   - {@link Logger.error `logger.error()`} - Logs an error message to the console.
 *   - {@link Logger.disable `logger.disable()`} - Disables all subscriptions to the logger.
 *   - {@link Logger.enable `logger.enable()`} - Enables all subscriptions to the logger.
 *   - {@link Logger.subscribe `logger.subscribe()`} - Adds a subscription to the logger.
 *
 * @param {boolean} enabled This determines whether or not subscriptions to the logger are
 * enabled/disabled.
 *
 * @param {string} namespace This provides context to distinguish which logger is firing if multiple
 * loggers are in use.
 *
 * @example
 *
 * ```tsx
 * import { createLogger } from '@iheartradio/web.utilities';
 *
 * const logger = createLogger({
 *   enabled: true,
 *   namespace: 'my-namespace',
 *   pretty: true,
 * });
 *
 * logger.log('Hello, world!');
 * ```
 */
function createLogger({
  enabled = true,
  level = Level.Log,
  namespace = '',
  pretty = false
} = {}) {
  function createLogFn(type) {
    return async function logFn(message, data, tags = []) {
      return {
        data,
        message,
        tags: namespace ? [namespace, ...tags] : tags,
        timestamp: new Date().toISOString(),
        type
      };
    };
  }
  const logger = createEmitter({
    error: createLogFn(Type.Error),
    info: createLogFn(Type.Info),
    log: createLogFn(Type.Log),
    warn: createLogFn(Type.Warn)
  });
  if (enabled === false) {
    logger.disable();
  }
  logger.subscribe({
    all(key, log) {
      if (log.type === Type.Log && level === Level.Log || log.type === Type.Info && level <= Level.Info || log.type === Type.Warn && level <= Level.Warn || log.type === Type.Error && level <= Level.Error) {
        if (pretty) {
          if (isUndefined(globalThis?.window)) {
            const title = ` ${log.type}${namespace ? `:${namespace}` : ''} `;
            const message = `${log.message} ${JSON.stringify(log, null, 2)} \n`;
            console[log.type](title, message);
          } else {
            const color = {
              [Type.Error]: {
                backgroundColor: '#F94E5C',
                borderColor: '#E22C3A',
                color: '#FFFFFF'
              },
              [Type.Info]: {
                backgroundColor: '#5FBBF0',
                borderColor: '#3CA2DD',
                color: '#FFFFFF'
              },
              [Type.Log]: {
                backgroundColor: '#96979F',
                borderColor: '#717277',
                color: '#FFFFFF'
              },
              [Type.Warn]: {
                backgroundColor: '#FDDF96',
                borderColor: '#FDCC5D',
                color: '#55565B'
              }
            }[log.type];
            const styles = `
              background-color: ${color.backgroundColor};
              border-radius: 2px;
              border: 1px solid ${color.borderColor};
              color: ${color.color};
              padding: 3px 6px;
            `;
            const title = `${log.type}${namespace ? `:${namespace}` : ''}`;
            console.groupCollapsed(`%c${title}%c ${log.message}`, styles, '');
            console.groupCollapsed('Data');
            console.log(log.data);
            console.groupEnd();
            console.groupCollapsed('Log');
            console.log(log);
            console.groupEnd();
            console.groupCollapsed('Trace');
            console.trace();
            console.groupEnd();
            console.groupEnd();
          }
        } else {
          console[log.type](log);
        }
      }
    }
  });
  return logger;
}

function createStorage(schema) {
  const storage = createEmitter(schema);
  return storage;
}

function createMemoryStorage(seed) {
  const cache = new Map(Object.entries(seed));
  const memoryStorage = createStorage({
    deserialize() {
      return Object.fromEntries(cache);
    },
    get(key) {
      return cache.get(key);
    },
    has(key) {
      return !!cache.get(key);
    },
    remove(key) {
      cache.delete(key);
    },
    serialize(data) {
      const entries = Object.entries(data);
      for (const [key, value] of entries) {
        cache.set(key, value);
      }
    },
    seed,
    set(key, value) {
      cache.set(key, value);
    },
    size() {
      return cache.size;
    }
  });
  return memoryStorage;
}

class StorageError extends Error {
  constructor(message, data) {
    super(message);
    this.data = data;
    this.message = message;
    this.name = 'StorageError';
  }
}

function createWebStorage({
  deserializer = JSON.parse,
  logger = createLogger(),
  serializer = JSON.stringify,
  seed,
  prefix = '',
  type
}) {
  const IS_SERVER = isUndefined(globalThis?.document);
  const MAX_COOKIE_BYTE_SIZE = 4096;
  const stores = {
    cookie: {
      get(key) {
        const value = cookie.get(prefix + key);
        if (isUndefined(value) || value === 'undefined') {
          return;
        }
        return deserializer(value);
      },
      remove(key) {
        cookie.remove(prefix + key);
      },
      set(key, value) {
        const serialized = serializer(value);
        const {
          size
        } = new Blob([serialized]);
        if (size > MAX_COOKIE_BYTE_SIZE) {
          throw new StorageError(`The cookie with the key of "${key}" is too large. It has a byte size of "${size}" - the limit is "${MAX_COOKIE_BYTE_SIZE}".`, {
            key,
            value,
            size
          });
        }
        cookie.set(prefix + key, serialized);
      }
    },
    local: {
      get(key) {
        const value = window.localStorage.getItem(prefix + key);
        if (isNull(value) || value === 'undefined') {
          return;
        }
        return deserializer(value);
      },
      remove(key) {
        window.localStorage.removeItem(prefix + key);
      },
      set(key, value) {
        window.localStorage.setItem(prefix + key, serializer(value));
      }
    },
    session: {
      get(key) {
        const value = window.sessionStorage.getItem(prefix + key);
        if (isNull(value) || value === 'undefined') {
          return;
        }
        return deserializer(value);
      },
      remove(key) {
        window.sessionStorage.removeItem(prefix + key);
      },
      set(key, value) {
        window.sessionStorage.setItem(prefix + key, serializer(value));
      }
    }
  };
  const storage = stores[type];
  const webStorage = createStorage({
    deserialize() {
      return Object.keys(seed).reduce((accumulator, key) => {
        try {
          return {
            ...accumulator,
            [key]: storage.get(key)
          };
        } catch (error) {
          logger.error(`Failed to deserialize "${key}".`, error);
          throw error;
        }
      }, {});
    },
    get(key) {
      try {
        return storage.get(key);
      } catch (error) {
        logger.error(`Failed to get "${key}".`, error);
        throw error;
      }
    },
    has(key) {
      try {
        return !!storage.get(key);
      } catch (error) {
        logger.error(`Failed to check the existence of "${key}".`, error);
        throw error;
      }
    },
    remove(key) {
      try {
        storage.remove(key);
      } catch (error) {
        logger.error(`Failed to remove "${key}".`, error);
        throw error;
      }
    },
    seed,
    serialize(data) {
      for (const [key, value] of Object.entries(data)) {
        try {
          storage.set(key, value);
        } catch (error) {
          logger.error(`Failed to serialize "${key}".`, error);
          throw error;
        }
      }
    },
    set(key, value) {
      try {
        storage.set(key, value);
      } catch (error) {
        logger.error(`Failed to set "${key}".`, error);
        throw error;
      }
    },
    size() {
      return Object.keys(seed).length;
    }
  });
  const memoryStorage = createMemoryStorage(seed);
  memoryStorage.subscribe({
    deserialize: webStorage.deserialize,
    get: (_, key) => webStorage.get(key),
    has: (_, key) => webStorage.has(key),
    remove: (_, key) => webStorage.remove(key),
    serialize: (_, data) => webStorage.serialize(data),
    set: (_, key, value) => webStorage.set(key, value),
    size: webStorage.size
  });
  if (IS_SERVER) {
    memoryStorage.disable();
  } else {
    const entries = Object.entries(webStorage.deserialize());
    const data = entries.reduce((accumulator, [key, value]) => ({
      ...accumulator,
      [key]: value ?? seed[key]
    }), {});
    memoryStorage.serialize(data);
  }
  return memoryStorage;
}

var types = /*#__PURE__*/Object.freeze({
  __proto__: null,
  CreateStorage: types
});

/**
 * @remarks {@link isFalsy isFalsy()} checks if a given value is falsy
 *
 * @param {unknown} value The value to check
 *
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isFalsy(null); // true
 * isFalsy(undefined); // true
 * isFalsy(0); // true
 * isFalsy(false); // true
 * isFalsy(''); // true
 * isFalsy(NaN); // true
 * isFalsy(true); // false
 * ```
 */
const isFalsy = value => !value;

/**
 * @remarks {@link isEmptyCollection isEmptyCollection()} checks if a collection is empty
 *
 * @param {unknown} value The value to check
 *
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isEmptyCollection([]); // true
 * isEmptyCollection({}); // true
 * isEmptyCollection([1]); // false
 * isEmptyCollection({a: 'b'}); //false
 * ```
 */
const isEmptyCollection = value => (Array.isArray(value) || value === Object(value)) && !(value instanceof Date) && !(value instanceof Set) && !(value instanceof Map) && Object.keys(value).length === 0;

/**
 * @remarks {@link isWhitespaceString isWhitespaceString()} checks if a string is only whitespace
 *
 * @param {unknown} value The value to check
 *
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isWhitespaceString(' '); // true
 * isWhitespaceString('\t\n\r'); // true
 * isWhitespaceString('hello'); // false
 * ```
 */
const isWhitespaceString = value => typeof value === 'string' && /^\s*$/.test(value);

/**
 * @remarks {@link isZeroLengthString isZeroLengthString()} checks if a string is zero length
 * @param {unknown} value The value to check
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isZeroLengthString(''); // true
 * isZeroLengthString(' '); // false
 * isZeroLengthString('hello'); // false
 * ````
 */
const isZeroLengthString = value => typeof value === 'string' && value.length === 0;

/**
 * @remarks {@link isInvalidDate isInvalidDate()} checks if an object is an invalid Date
 *
 * @param {unknown} value The value to check
 *
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isInvalidDate(new Date('hello')); // true
 * isInvalidDate(new Date()); // false
 * ```
 */
const isInvalidDate = value => value instanceof Date && Number.isNaN(value.getTime());

/**
 * @remarks {@link isEmptySet isEmptySet()} checks if an object is an empty Set
 *
 * @param {unknown} value The value to check
 *
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isEmptySet(new Set()); // true
 * isEmptySet(new Set([1])); // false
 * ```
 */
const isEmptySet = value => value instanceof Set && value.size === 0;

/**
 * @remarks {@link isEmptyMap isEmptyMap()} checks if an object is an empty Map
 *
 * @param {unknown} value The value to check
 *
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isEmptyMap(new Map()); // true
 * isEmptyMap(new Map([[1, 'one']])); //false
 * ```
 */
const isEmptyMap = value => value instanceof Map && value.size === 0;

/**
 * @remarks {@link isBlank isBlank()} checks if a value is 'blank'
 *
 * @param {unknown} value The value to check
 *
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isBlank(null); // true
 * isBlank(undefined); // true
 * isBlank(0); // true
 * isBlank(false); // true
 * isBlank(''); // true
 * isBlank(' \r\n'); //true
 * isBlank(NaN); // true
 * isBlank([]); // true
 * isBlank({}); // true
 * isBlank(new Date('hello')); // true
 * isBlank(new Set()); // true
 * isBlank(new Map()); // true
 * ```
 */
const isBlank = value => {
  if (isFalsy(value)) return true;
  if (isWhitespaceString(value)) return true;
  if (isEmptySet(value)) return true;
  if (isEmptyMap(value)) return true;
  if (isInvalidDate(value)) return true;
  return isEmptyCollection(value);
};

/**
 * @remarks {@link isNotBlank isNotBlank()} checks if a value is not 'blank'
 *
 * @param {uknown} value The value to check
 *
 * @returns boolean
 *
 * @example
 * ```ts
 * isNotBlank(null); // false
 * isNotBlank(undefined); // false
 * isNotBlank(0); // false
 * isNotBlank(false); // false
 * isNotBlank(''); // false
 * isNotBlank(' \r\n'); //false
 * isNotBlank(NaN); // false
 * isNotBlank([]); // false
 * isNotBlank({}); // false
 * isNotBlank(new Date('hello')); // false
 * isNotBlank(new Set()); // false
 * isNotBlank(new Map()); // false
 * isNotBlank(1); // true
 * isNotBlank('hello world'); // true
 * isNotBlank({ foo: bar }); // true
 * isNotBlank([1,2,3]); // true
 * isNotBlank(new Set([1,2,3])); // true
 * isNotBlank(new Map([['foo', 'bar'], ['baz', 'buzz']])); // true
 * isNotBlank(new Date()); // true
 * ```
 */

const isNotBlank = value => {
  return !isFalsy(value) && !isWhitespaceString(value) && !isEmptySet(value) && !isEmptyMap(value) && !isInvalidDate(value) && !isEmptyCollection(value);
};

// Stolen from https://github.com/sindresorhus/is-plain-obj
function isPlainObject(value) {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return (prototype === null || prototype === Object.prototype || Object.getPrototypeOf(prototype) === null) && !(Symbol.toStringTag in value) && !(Symbol.iterator in value);
}

const hashEmail = async email => {
  const utf8 = new TextEncoder().encode(email);
  const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(bytes => bytes.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

function identity(x, ..._args) {
  return x;
}
function identityP(x, ..._args) {
  return Promise.resolve(x);
}

const {
  isBase64,
  isJWT,
  isLocale
} = validator;

function loadScript(source, options) {
  if (isUndefined(globalThis?.document)) {
    throw new Error('Scripts can only be loaded within a browser context.');
  }
  const {
    async,
    crossorigin,
    defer,
    id,
    integrity,
    nomodule,
    referrerpolicy,
    replace = false,
    target = globalThis.document.body
  } = options ?? {};
  const dataAttributes = Object.entries(options ?? {}).reduce((dataset, [key, value]) => {
    if (key.startsWith('data-')) {
      dataset[key] = value;
    }
    return dataset;
  }, {});
  return new Promise((resolve, reject) => {
    if (document.querySelector(`#${id}`)) {
      if (!replace) {
        resolve();
        return;
      } else {
        document.querySelector(`#${id}`)?.remove();
      }
    }
    const script = document.createElement('script');
    script.setAttribute('src', source);
    script.setAttribute('type', 'text/javascript');
    if (id) script.setAttribute('id', id);
    if (async) script.setAttribute('async', 'true');
    if (crossorigin) script.setAttribute('crossOrigin', crossorigin);
    if (defer) script.setAttribute('defer', 'true');
    if (integrity) script.setAttribute('integrity', integrity);
    if (nomodule) script.setAttribute('noModule', 'true');
    if (referrerpolicy) script.setAttribute('referrerPolicy', referrerpolicy);
    script.addEventListener('load', resolve);
    script.addEventListener('error', reject);
    if (!isEmptyCollection(dataAttributes)) {
      for (const [key, value] of Object.entries(dataAttributes)) {
        script.setAttribute(key, String(value));
      }
    }
    target.append(script);
  });
}

const MEDIA_SERVER_URL = 'https://i.iheart.com';
const CATALOG_BASE = 'v3/catalog';
const buildOpsArray = imageOps => {
  const operations = [];
  for (const operation of imageOps) {
    for (const [key, value] of Object.entries(operation)) {
      if (isString(value)) {
        operations.push(`${key}(${value.replaceAll(/\s+/g, '')})`);
      } else if (value === true) {
        operations.push(`${key}()`);
      } else {
        operations.push(`${key}(${value})`);
      }
    }
  }
  return operations;
};
const getExistingOps = searchParms => {
  if (!searchParms.has('ops')) return [];
  const opsParameter = searchParms.get('ops');
  const existingOpsArray = decodeURIComponent(opsParameter).split(/(.*?\)),/).filter(element => element !== '');
  const existingOpsMap = new Map();
  for (const op of existingOpsArray) {
    const [key, value] = op.split('(');
    const valueTrimmed = value.split(')')[0].replaceAll(/\s+/g, '');
    // 'blur' is the only ImageOperation that takes a true number
    if (key === 'blur') {
      existingOpsMap.set(key, {
        [key]: Number(valueTrimmed)
      });
      // 'grey' is the only ImageOperation that takes a true boolean
    } else if (key === 'grey') {
      existingOpsMap.set(key, {
        [key]: true
      });
    } else {
      existingOpsMap.set(key, {
        [key]: valueTrimmed
      });
    }
  }
  return [...existingOpsMap.values()];
};
const mergeOps = (existingOps, opsToApply) => {
  const opsHash = new Map();
  for (const op of existingOps) {
    for (const key of Object.keys(op)) {
      opsHash.set(key, op);
    }
  }
  for (const op of opsToApply) {
    for (const key of Object.keys(op)) {
      opsHash.set(key, op);
    }
  }
  return [...opsHash.values()];
};
const mediaServerImage = memoize(({
  type,
  id,
  imageSource,
  imageOps = [{
    fit: '240,240'
  }],
  metadata = false
}) => {
  const searchParams = new URLSearchParams();
  const opsToApply = buildOpsArray(imageOps);
  searchParams.set('ops', opsToApply.join(','));
  if (metadata) {
    searchParams.append('metadata', '1');
  }
  // This is very important! From https://docs.radioedit.iheart.com/mediaserver/catalog-api
  // This will ensure that MediaServer doesn't issue a 302 redirect and instead just
  // return the resource. MASSIVELY improves performance
  searchParams.append('stay', '1');
  if (isNotBlank(type) && isNotBlank(id)) {
    const mediaServerImageUrl = new URL(MEDIA_SERVER_URL);
    if (type === 'favorites') {
      mediaServerImageUrl.pathname = `v3/user/${id}/profile`;
      const favoriteOps = buildOpsArray([{
        run: '"favorite"'
      }, ...imageOps]);
      searchParams.set('ops', favoriteOps.join(','));
    } else {
      mediaServerImageUrl.pathname = `${CATALOG_BASE}/${type}/${id}`;
    }
    for (const [key, value] of searchParams.entries()) {
      mediaServerImageUrl.searchParams.set(key, value);
    }
    return mediaServerImageUrl.toString();
  } else {
    if (!isNotBlank(imageSource)) return '';
    try {
      const imageUrl = new URL(imageSource);
      if (imageUrl.hostname === 'i.iheart.com') {
        const existingOps = getExistingOps(imageUrl.searchParams);
        const newOps = buildOpsArray(mergeOps(existingOps, imageOps));
        searchParams.set('ops', newOps.join(','));
        return `https://${imageUrl.hostname}${imageUrl.pathname}?${searchParams.toString()}`;
      } else {
        const imageString = btoa(imageUrl.toString());
        return `https://i.iheart.com/v3/url/${imageString}?${searchParams.toString()}`;
      }
    } catch {
      return '';
    }
  }
});

const stringifyValues = o => {
  return Object.fromEntries(Object.entries(o).filter(([_key, value]) => !isNil(value)).map(([key, value]) => [key, String(value)]));
};

function slugify(value) {
  // Do some cleanup first
  const cleanString = value.replace("'", '').toLocaleLowerCase();
  return baseSlugify(cleanString);
}

function take(list, n) {
  const result = Array.from({
    length: n
  });
  for (let index = 0; index < n; index++) {
    result[index] = list[index];
  }
  return result;
}

function toHHMMSS(time) {
  if (!Number.isFinite(time) || time < 0) return null;
  const date = new Date(0);
  date.setSeconds(time);
  return date.toISOString().slice(11, 19).replace(/^00:0?|^0/, '');
}

const toURL = input => {
  if (input instanceof URL) {
    return input;
  }
  return new URL(input);
};
const toHttps = input => {
  const url = toURL(input);
  url.protocol = 'https:';
  return url;
};

function useIsOnline() {
  const [isOnline, setIsOnline] = useState(isUndefined(globalThis?.window) ? true : window.navigator.onLine);
  useEffect(() => {
    function setOnline() {
      setIsOnline(window.navigator.onLine);
    }
    window.addEventListener('load', setOnline);
    window.addEventListener('online', setOnline);
    window.addEventListener('offline', setOnline);
    return function cleanup() {
      window.removeEventListener('load', setOnline);
      window.removeEventListener('online', setOnline);
      window.removeEventListener('offline', setOnline);
    };
  }, []);
  return isOnline;
}

function waitUntil(condition, timeout = 10_000) {
  const time = Date.now();
  let id;
  function callback(resolve, reject) {
    if (condition()) {
      window.cancelAnimationFrame(id);
      resolve();
      return;
    }
    if (Date.now() - time >= timeout) {
      window.cancelAnimationFrame(id);
      reject(new Error('Timeout exceeded.'));
      return;
    }
    id = window.requestAnimationFrame(() => callback(resolve, reject));
  }
  return new Promise(callback);
}

export { types as CreateStorage, Level, Type, createLogger, createMemoryStorage, createStorage, createWebStorage, hashEmail, identity, identityP, isBase64, isBlank, isEmptyCollection, isEmptyMap, isEmptySet, isFalsy, isInvalidDate, isJWT, isLocale, isNotBlank, isPlainObject, isWhitespaceString, isZeroLengthString, loadScript, mediaServerImage, slugify, stringifyValues, take, toHHMMSS, toHttps, toURL, useIsOnline, waitUntil };
